import React from 'react';
import { Divide, Calendar, TrendingUp, BarChart3, Loader2 } from 'lucide-react';
import type { SpreadPattern } from '../../api';
// eslint-disable-next-line import/no-relative-packages
import { formatCurrencyRounded } from '../../../../utils/currency';

interface SpreadPatternSelectorProps {
  selectedPattern: SpreadPattern | null;
  onSelect: (pattern: SpreadPattern) => void;
  onApply: () => void;
  isApplying: boolean;
  totalAmount: number;
  currency?: string | null;
  hasLyData?: boolean;
  hasOtbData?: boolean;
}

interface PatternOption {
  id: SpreadPattern;
  label: string;
  description: string;
  icon: React.ReactNode;
  disabled?: boolean;
  disabledReason?: string;
}

// Local helper preserves the previous (currency, opts) call site shape used
// just below. The shared util enforces per-currency fraction rules.
const formatCurrencyLocal = (value: number, currency?: string | null): string =>
  formatCurrencyRounded(value, currency);

export function SpreadPatternSelector({
  selectedPattern,
  onSelect,
  onApply,
  isApplying,
  totalAmount,
  currency,
  hasLyData = true,
  hasOtbData = true,
}: SpreadPatternSelectorProps) {
  const patterns: PatternOption[] = [
    {
      id: 'even',
      label: 'Even',
      description: 'Divide equally across all days',
      icon: <Divide size={18} />,
    },
    {
      id: 'weekend',
      label: 'Weekend',
      description: 'Higher on Fri/Sat/Sun',
      icon: <Calendar size={18} />,
    },
    {
      id: 'ly',
      label: 'Last Year',
      description: 'Match LY daily pattern',
      icon: <TrendingUp size={18} />,
      disabled: !hasLyData,
      disabledReason: 'No LY data available',
    },
    {
      id: 'otb',
      label: 'OTB',
      description: 'Match on-the-books pattern',
      icon: <BarChart3 size={18} />,
      disabled: !hasOtbData,
      disabledReason: 'No OTB data available',
    },
  ];

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <h3 className="text-sm font-semibold text-slate-900 mb-3">
        Spread Pattern
      </h3>

      {/* Total amount display */}
      <div className="mb-4 p-3 bg-slate-50 rounded-lg">
        <div className="text-xs text-slate-500 mb-1">Amount to spread</div>
        <div className="text-xl font-semibold text-slate-900">
          {formatCurrencyLocal(totalAmount, currency)}
        </div>
      </div>

      {/* Pattern options */}
      <div className="space-y-2 mb-4">
        {patterns.map((pattern) => (
          <button
            key={pattern.id}
            onClick={() => !pattern.disabled && onSelect(pattern.id)}
            disabled={pattern.disabled}
            className={`
              w-full p-3 rounded-lg border-2 text-left transition-all
              flex items-start gap-3
              ${pattern.disabled
                ? 'border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed'
                : selectedPattern === pattern.id
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }
            `}
          >
            <div
              className={`
                p-2 rounded-lg
                ${selectedPattern === pattern.id
                  ? 'bg-blue-100 text-blue-600'
                  : 'bg-slate-100 text-slate-500'
                }
              `}
            >
              {pattern.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div
                className={`
                  font-medium text-sm
                  ${selectedPattern === pattern.id ? 'text-blue-900' : 'text-slate-900'}
                `}
              >
                {pattern.label}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {pattern.disabled ? pattern.disabledReason : pattern.description}
              </div>
            </div>
            {selectedPattern === pattern.id && (
              <div className="w-2 h-2 rounded-full bg-blue-500 mt-2" />
            )}
          </button>
        ))}
      </div>

      {/* Apply button */}
      <button
        onClick={onApply}
        disabled={!selectedPattern || isApplying || totalAmount === 0}
        className={`
          w-full py-2.5 px-4 rounded-lg font-medium text-sm
          flex items-center justify-center gap-2
          ${selectedPattern && !isApplying && totalAmount > 0
            ? 'bg-blue-600 text-white hover:bg-blue-700'
            : 'bg-slate-100 text-slate-400 cursor-not-allowed'
          }
        `}
      >
        {isApplying ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Spreading...
          </>
        ) : (
          <>Apply Pattern</>
        )}
      </button>

      {totalAmount === 0 && (
        <p className="mt-2 text-xs text-amber-600 text-center">
          Enter a total amount first
        </p>
      )}
    </div>
  );
}
